import type {
  MotionElement,
  MotionPatch,
  MotionScene,
  MotionValidationResult,
} from "./types";
import { sanitizeMotionScene } from "./sanitizeMotionScene";
import { setMotionProperty } from "./interpolation";
export function patchMotionScene(
  scene: MotionScene,
  patches: MotionPatch[],
): MotionValidationResult {
  const fail = (
    path: string,
    code = "invalid_patch",
  ): MotionValidationResult => ({
    scene: null,
    issues: [{ path, code, severity: "error" }],
  });
  if (!Array.isArray(patches) || patches.length > 150) return fail("$.patches");
  const candidate = JSON.parse(JSON.stringify(scene)) as MotionScene;
  const find = (
    elements: MotionElement[],
    id: string,
  ): {
    element: MotionElement;
    siblings: MotionElement[];
    index: number;
  } | null => {
    for (let index = 0; index < elements.length; index++) {
      const element = elements[index];
      if (element.id === id) return { element, siblings: elements, index };
      const child = find(element.children ?? [], id);
      if (child) return child;
    }
    return null;
  };
  for (let i = 0; i < patches.length; i++) {
    const patch = patches[i];
    const p = `$.patches[${i}]`;
    if (
      !patch ||
      typeof patch !== "object" ||
      !["set", "remove", "insert"].includes(patch.op) ||
      typeof patch.element !== "string" ||
      typeof patch.path !== "string"
    )
      return fail(p);
    const parts = patch.path.split(".");
    if (
      parts.length > 12 ||
      parts.some((k) => /^\d+$/.test(k) && Number(k) > 1000) ||
      parts.some((k) =>
        ["__proto__", "constructor", "prototype"].includes(k),
      ) ||
      (patch.path && !/^[\w]+(?:\.[\w]+)*$/.test(patch.path))
    )
      return fail(`${p}.path`, "unsafe_patch_path");
    const target = find(candidate.elements, patch.element);
    if (!target) return fail(`${p}.element`, "element_not_found");
    if (!patch.path) {
      if (patch.op === "remove") target.siblings.splice(target.index, 1);
      else if (patch.op === "insert")
        target.siblings.splice(
          target.index + 1,
          0,
          patch.value as MotionElement,
        );
      else target.siblings[target.index] = patch.value as MotionElement;
      continue;
    }
    if (patch.op === "set")
      setMotionProperty(target.element, patch.path, patch.value);
    else {
      let parent: unknown = target.element;
      for (const key of parts.slice(0, -1)) {
        if (!parent || typeof parent !== "object") return fail(`${p}.path`);
        parent = (parent as Record<string, unknown>)[key];
      }
      if (!parent || typeof parent !== "object") return fail(`${p}.path`);
      const key = parts[parts.length - 1];
      if (patch.op === "remove") {
        if (Array.isArray(parent) && /^\d+$/.test(key))
          parent.splice(Number(key), 1);
        else delete (parent as Record<string, unknown>)[key];
      } else if (
        Array.isArray(parent) &&
        /^\d+$/.test(key) &&
        Number(key) <= parent.length
      )
        parent.splice(Number(key), 0, patch.value);
      else {
        const array = (parent as Record<string, unknown>)[key];
        if (!Array.isArray(array)) return fail(`${p}.path`);
        array.push(patch.value);
      }
    }
  }
  return sanitizeMotionScene(candidate);
}
